<template>
    <div>
        <button
            ref="buttonRef"
            type="button"
            class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-gray-300 text-gray-600 transition hover:bg-gray-50"
            :aria-label="t('vue.ai_chat_capabilities_help')"
            :title="t('vue.ai_chat_capabilities_help')"
            @click="toggle"
        >
            <QuestionMarkCircleIcon class="h-5 w-5" />
        </button>

        <!-- Teleported so the chat panel's overflow never clips the popover. -->
        <Teleport to="body">
            <template v-if="open">
                <div class="fixed inset-0 z-[590]" @click="open = false" />
                <div
                    class="fixed z-[600] w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg"
                    :style="menuStyle"
                >
                    <p
                        class="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400"
                    >
                        {{ t("vue.ai_chat_capabilities_title") }}
                    </p>
                    <ul class="grid gap-0.5">
                        <li
                            v-for="capability in capabilities"
                            :key="capability.label"
                        >
                            <!-- Each line doubles as a shortcut: it names what the
                                 assistant can do and carries the question that asks
                                 for it. -->
                            <button
                                type="button"
                                :disabled="disabled"
                                class="group flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition"
                                :class="
                                    disabled
                                        ? 'cursor-not-allowed text-gray-400'
                                        : 'cursor-pointer text-gray-700 hover:bg-[#FF421E]/5 hover:text-[#FF421E]'
                                "
                                :title="capability.prompt"
                                @click="select(capability)"
                            >
                                <CheckIcon
                                    class="mt-0.5 h-4 w-4 shrink-0"
                                    :class="
                                        disabled
                                            ? 'text-gray-300'
                                            : 'text-green-500'
                                    "
                                />
                                <span class="flex-1">{{
                                    capability.label
                                }}</span>
                                <ArrowRightIcon
                                    v-if="!disabled"
                                    class="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-0 transition group-hover:opacity-100"
                                />
                            </button>
                        </li>
                    </ul>
                </div>
            </template>
        </Teleport>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import {
    QuestionMarkCircleIcon,
    CheckIcon,
    ArrowRightIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

withDefaults(defineProps<{ disabled?: boolean }>(), { disabled: false });

const emit = defineEmits<{
    (event: "select", prompt: string): void;
}>();

const open = ref(false);
const buttonRef = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

const toggle = (): void => {
    if (open.value) {
        open.value = false;
        return;
    }
    const rect = buttonRef.value?.getBoundingClientRect();
    if (rect) {
        menuStyle.value = {
            top: `${rect.bottom + 4}px`,
            right: `${window.innerWidth - rect.right}px`,
        };
    }
    open.value = true;
};

interface ICapability {
    label: string;
    prompt: string;
}

const capabilities = computed((): ICapability[] =>
    [
        "dashboard",
        "domains",
        "dns_lookup",
        "reports",
        "blacklists",
        "ip_geo",
        "alerts",
        "team",
        "billing",
    ].map((key) => ({
        label: t(`vue.ai_chat_cap_${key}`),
        prompt: t(`vue.ai_chat_cap_${key}_prompt`),
    })),
);

const select = (capability: ICapability): void => {
    open.value = false;
    emit("select", capability.prompt);
};
</script>
